import { useCMSPageField } from '@/cms/fields'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { ProductAdsComponent } from '@/scopes/product-ads/components/ProductAdsLegacy'
import { useProductAds } from '@/scopes/product-ads/domain/hooks'
import { ProductAdComponentType } from '@/scopes/product-ads/domain/types'
import { Button, ButtonLoaderIcon, tk, Typography } from '@sunwebgroup/ui-components'
import * as React from 'react'
import { css, styled } from 'styled-components'

interface ProductAdsBannerProps {
  componentType: Extract<ProductAdComponentType, 'LandingBanner' | 'SearchBanner'>
  compact?: boolean
}

function compactMod() {
  return css`
    @media ${tk('mq_tablet')} {
      height: 135px;
    }
    @media ${tk('mq_desktop_sm')} {
      height: 143px;
    }
  `
}

const Component = styled(ProductAdsComponent)<{ compact?: boolean }>`
  margin: ${tk('spacing_3xl')} 0;
  ${({ compact }) =>
    compact
    && css`
      margin-top: 0;
    `}
`

const Wrapper = styled.div<{ compact?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 303px;
  background: ${tk('color_semantic_bg_disabled')};
  border-radius: ${tk('radius_large')};
  @media ${tk('mq_tablet')} {
    height: 236px;
    flex-direction: row;
  }
  @media ${tk('mq_desktop_sm')} {
    height: 267px;
  }
  ${({ compact }) => compact && compactMod()}
`

const BackgroundImage = styled.img<{ compact?: boolean }>`
  object-fit: cover;
  width: 100%;
  height: 303px;
  border-radius: ${tk('radius_large')};
  @media ${tk('mq_tablet')} {
    object-position: 50% 21%;
    height: 236px;
  }
  @media ${tk('mq_desktop_sm')} {
    height: 267px;
  }
  ${({ compact }) => compact && compactMod()}
`

const StyledLogo = styled.img`
  position: absolute;
  top: 26px;
  height: 68px;
  width: auto;
  @media ${tk('mq_tablet')} {
    top: ${tk('spacing_xl')};
    left: ${tk('spacing_xl')};
  }
  @media ${tk('mq_desktop_sm')} {
    top: ${tk('spacing_3xl')};
    left: ${tk('spacing_3xl')};
  }
`

const InfoBox = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  width: calc(100% - 24px);
  background: white;
  padding: ${tk('spacing_md')};
  position: absolute;
  bottom: ${tk('spacing_sm')};
  border-radius: 8px;
  @media ${tk('mq_tablet')} {
    height: fit-content;
    top: 0;
    bottom: 0;
    margin: auto 0 auto 0;
    width: 320px;
    right: ${tk('spacing_xl')};
    ${({ compact }) =>
      compact
      && css`
        flex-direction: row;
        justify-content: space-between;
        width: 480px;
        right: ${tk('spacing_sm')};
        align-items: center;
      `}
  }
  @media ${tk('mq_desktop_sm')} {
    right: ${tk('spacing_3xl')};
    ${({ compact }) =>
      compact
      && css`
        width: 640px;
        right: ${tk('spacing_md')};
      `}
  }
`

const InfoBoxBodyWrapper = styled.span<{ compact?: boolean }>`
  padding: 4px 0 ${tk('spacing_md')} 0;
  @media ${tk('mq_tablet')} {
    ${({ compact }) =>
      compact
      && css`
        padding: 4px 0 0 0;
      `}
  }
`

const InfoBoxContent = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  @media ${tk('mq_tablet')} {
    ${({ compact }) =>
      compact
      && css`
        width: 304px;
      `}
  }
  @media ${tk('mq_desktop_sm')} {
    ${({ compact }) =>
      compact
      && css`
        width: 464px;
      `}
  }
`
const ActionButton = styled(Button)<{ compact?: boolean }>`
  &:hover {
    color: white;
  }

  @media ${tk('mq_tablet')} {
    ${({ compact }) =>
      compact
      && css`
        width: 128px;
        text-align: center;
      `}
  }
  @media ${tk('mq_desktop_sm')} {
    ${({ compact }) =>
      compact
      && css`
        min-width: 201px;
        width: auto;
        text-align: center;
      `}
  }
`

export function ProductAdsBanner({ compact, componentType }: ProductAdsBannerProps) {
  const contextItemId = useCMSPageField('contextItemId')
  const { isLoading, isFetched, error, data } = useProductAds({ contextItemId, componentType })
  const intl = useIntl()
  const bannerData = data?.[0]

  if (isLoading && !isFetched) {
    return (
      <Component
        infoLabel={intl.formatMessage({ id: 'search_result_product_ads_label' })}
        infoContent={intl.formatMessage({ id: 'search_result_product_ads_content' })}
        compact={compact}
      >
        <Wrapper compact={compact}>
          <ButtonLoaderIcon />
        </Wrapper>
      </Component>
    )
  }

  if (error || !bannerData) {
    return null
  }

  return (
    <Component
      infoLabel={intl.formatMessage({ id: 'search_result_product_ads_label' })}
      infoContent={intl.formatMessage({ id: 'search_result_product_ads_content' })}
      compact={compact}
    >
      <Wrapper compact={compact}>
        <BackgroundImage
          compact={compact}
          src={buildImgSrc(bannerData?.image || '', 375)}
          srcSet={buildImgSrcSet(bannerData?.image || '', [375, 600, 720, 1440, 1440, 2000, 2400])}
          sizes="100vw"
          // @ts-expect-error -- Not part of the types yet, but this is an img attribute in modern browsers
          fetchpriority="high"
        />
        <StyledLogo src={bannerData?.logo || ''} />
        <InfoBox compact={compact}>
          <InfoBoxContent compact={compact}>
            <Typography $variant="heading-md" as="span">
              {bannerData?.heading}
            </Typography>
            <InfoBoxBodyWrapper compact={compact}>
              <Typography $variant="label-thin">{bannerData?.subHeading}</Typography>
            </InfoBoxBodyWrapper>
          </InfoBoxContent>
          <ActionButton
            compact={compact}
            $fullSize
            $hierarchy="primary"
            as="a"
            href={bannerData?.clicked}
            target="_blank"
            high={44}
          >
            {intl.formatMessage({ id: 'search_result_product_ads_banner_action_text' })}
          </ActionButton>
        </InfoBox>
      </Wrapper>
    </Component>
  )
}
